/**
 * Phase 101ea.20 (Feb 2026) — Préparation de tournée sur TABLETTE.
 *
 * Refonte du workflow PDF papier vers un écran interactif utilisable en cuisine
 * pendant la préparation du box thermo. Reprend la structure visuelle du PDF
 * `prep-picklist.pdf` (compacte, une section par distributeur) mais avec :
 *  - Saisie interactive de `qty_loaded` et `lot_number`
 *  - Autosave par ligne (debounce 500 ms)
 *  - Dropdown des lots dispos au Stock Conthey (pas de FIFO auto — choix opérateur)
 *  - Option scan clavier (input focus, entrée valide)
 *  - Cases à cocher visuelles pour tracker l'avancement
 *
 * Route : /refill-tours/:id/prep-tablet
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  ScanLine,
  FileDown,
  Package,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const AUTOSAVE_DEBOUNCE_MS = 500;

const fmtDlc = (dlc) => {
  if (!dlc) return "—";
  const iso = String(dlc).slice(0, 10);
  const [y, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : iso;
};

const daysToDlc = (dlc) => {
  if (!dlc) return null;
  const iso = String(dlc).slice(0, 10);
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor((t - now.getTime()) / (24 * 3600 * 1000));
};

// —— Row entry ——
function LineRow({
  line,
  productName,
  productSku,
  onSave,
  saveState,
  readOnly = false,
  nayaxRecoQty,       // Phase 101gd — reco Nayax (undefined = pas chargé, null = indispo, nombre = reco)
  nayaxRecoLoading,   // true tant que l'appel POST picklist est en cours
  nayaxRecoError,     // string si l'appel a échoué
}) {
  // Phase 101ea.20.2 — Affiche l'allocation existante (peu importe l'origine :
  // tablette prep ou ancien écran RefillTourDetail LOTS CONTHEY). Aucun code
  // backend n'écrit d'allocations automatiques → toutes les allocations en
  // base viennent d'un opérateur humain, donc légitimes à afficher.
  const initialAlloc = (line.lot_allocations || [])[0];
  const [qtyLoaded, setQtyLoaded] = useState(line.qty_loaded ?? 0);
  const [lotNumber, setLotNumber] = useState(initialAlloc?.lot_number || "");
  const [lotDlc, setLotDlc] = useState(initialAlloc?.dlc || "");
  const [lotId, setLotId] = useState(initialAlloc?.lot_id || "");
  const [availableLots, setAvailableLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [showScan, setShowScan] = useState(false);
  const scanRef = useRef(null);
  const saveTimer = useRef(null);

  const done = qtyLoaded > 0 && lotId;
  // Phase 101fj — Rouge « Lot ! » si l'opérateur a saisi une quantité sans lot
  const lotMissing = qtyLoaded > 0 && !lotId;
  const days = daysToDlc(lotDlc);
  const dlcCritical = days !== null && days <= 3;

  // Autosave debounce
  const scheduleSave = useCallback((partial) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onSave(line, partial);
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [line, onSave]);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  const loadLots = async () => {
    if (availableLots.length || lotsLoading) return;
    setLotsLoading(true);
    try {
      const { data } = await api.get(`/moma-refills/available-lots/${line.product_id}`);
      // Phase 101ea.20.1 — Tri neutre par n° de lot (alphabétique croissant,
      // ordre de création AM). PAS de FIFO ni LIFO automatique : Alain veut
      // que l'opérateur choisisse librement selon la destination (les distants
      // reçoivent les jeunes, Conthey absorbe les vieux — décision humaine).
      const lots = [...(data.lots || [])].sort((a, b) =>
        (a.lot_number || "").localeCompare(b.lot_number || "", "fr", { numeric: true })
      );
      setAvailableLots(lots);
    } catch (err) {
      toast.error(`Erreur chargement lots: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLotsLoading(false);
    }
  };

  const selectLot = (lot) => {
    setLotId(lot.id);
    setLotNumber(lot.lot_number);
    setLotDlc(lot.dlc || "");
    setPopoverOpen(false);
    scheduleSave({
      lot_allocations: [{
        lot_id: lot.id,
        lot_number: lot.lot_number,
        dlc: lot.dlc || null,
        qty: Number(qtyLoaded) || 0,
        manual: true,
      }],
    });
  };

  const handleScan = () => {
    const val = scanValue.trim();
    if (!val) return;
    // Cherche dans les lots dispos (charge d'abord si pas encore)
    const search = async () => {
      if (!availableLots.length) await loadLots();
      const found = availableLots.find(
        (l) => l.lot_number === val || l.lot_number.toUpperCase() === val.toUpperCase()
      );
      if (found) {
        selectLot(found);
        toast.success(`Lot ${found.lot_number} sélectionné`);
      } else {
        toast.error(`Lot "${val}" introuvable au Stock Conthey pour ce produit`);
      }
      setScanValue("");
      setShowScan(false);
    };
    search();
  };

  const changeQty = (val) => {
    const q = Math.max(0, Number(val) || 0);
    setQtyLoaded(q);
    const partial = { qty_loaded: q };
    if (lotId) {
      partial.lot_allocations = [{
        lot_id: lotId,
        lot_number: lotNumber,
        dlc: lotDlc || null,
        qty: q,
        manual: true,
      }];
    }
    scheduleSave(partial);
  };

  return (
    <tr
      className={`border-b border-[#E5E5E0] transition-colors ${
        lotMissing ? "bg-[#FBE9E4]" : done ? "bg-[#EAF4EE]/40" : ""
      }`}
      data-testid={`prep-line-${line.distributor_id}-${line.slot_number}-${line.product_id}`}
    >
      <td className="px-2 py-3 text-center font-mono font-bold text-[#1F4E3D] w-14">
        {line.slot_number || "—"}
      </td>
      <td className="px-2 py-3">
        <div className="font-medium text-[#1A1C1A] leading-tight">{productName}</div>
        <div className="text-xs text-[#A3A6A3] font-mono">{productSku}</div>
      </td>
      <td className="px-2 py-3 text-center text-sm text-[#575A57] w-16">{line.current_qty ?? 0}</td>
      <td className="px-2 py-3 text-center text-sm text-[#575A57] w-16">{line.capacity_max ?? 0}</td>
      {/* Phase 101gd — Reco Nayax (POST picklist algo intelligent) */}
      <td className="px-2 py-3 text-center w-20" data-testid={`prep-nayax-reco-${line.distributor_id}-${line.slot_number}`}>
        {nayaxRecoLoading ? (
          <span className="text-[10px] text-[#A3A6A3]">…</span>
        ) : nayaxRecoError ? (
          <span className="text-[10px] text-[#C05C47]" title={nayaxRecoError}>N/A</span>
        ) : nayaxRecoQty === undefined ? (
          <span className="text-[10px] text-[#A3A6A3]">—</span>
        ) : (() => {
          const planned = line.qty_planned ?? 0;
          const diff = nayaxRecoQty - planned;
          const bigDiff = Math.abs(diff) >= 3;
          const color = bigDiff ? "text-[#C05C47] font-bold" : "text-[#5B7A5F] font-semibold";
          return (
            <div className={`text-sm ${color}`} title={`Nayax recommande +${nayaxRecoQty} · AgroManage prévoit +${planned}${diff !== 0 ? ` (écart ${diff > 0 ? '+' : ''}${diff})` : ''}`}>
              +{nayaxRecoQty}
              {bigDiff && diff !== 0 && (
                <div className="text-[9px] font-normal opacity-80">écart {diff > 0 ? "+" : ""}{diff}</div>
              )}
            </div>
          );
        })()}
      </td>
      <td className="px-2 py-3 text-center text-sm font-bold text-[#1F4E3D] w-16">
        {line.qty_planned ?? 0}
      </td>
      {/* Qté chargée — input */}
      <td className="px-2 py-3 text-center w-20">
        <Input
          type="number"
          inputMode="numeric"
          min="0"
          value={qtyLoaded}
          onChange={(e) => changeQty(e.target.value)}
          disabled={readOnly}
          className="w-16 text-center text-base font-bold h-11"
          data-testid={`prep-qty-loaded-${line.distributor_id}-${line.slot_number}`}
        />
      </td>
      {/* Lot — Popover picker + scan */}
      <td className="px-2 py-3 w-44">
        <Popover open={popoverOpen} onOpenChange={(o) => { if (readOnly) return; setPopoverOpen(o); if (o) loadLots(); }}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              disabled={readOnly}
              className={`w-full h-11 justify-start font-mono text-sm ${lotMissing ? "border-[#C05C47] bg-[#FBE9E4] text-[#7A1F1F]" : ""}`}
              data-testid={`prep-lot-btn-${line.distributor_id}-${line.slot_number}`}
            >
              {lotNumber ? (
                lotNumber
              ) : lotMissing ? (
                <span className="flex items-center gap-1 font-bold">
                  <AlertTriangle className="w-4 h-4" /> Sélection manuelle requise
                </span>
              ) : (
                <span className="text-[#A3A6A3]">Choisir…</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="p-2 border-b border-[#E5E5E0]">
              <button
                type="button"
                onClick={() => { setShowScan((s) => !s); setTimeout(() => scanRef.current?.focus(), 50); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-[#1F4E3D] hover:bg-[#EAF4EE] rounded"
                data-testid={`prep-scan-toggle-${line.distributor_id}-${line.slot_number}`}
              >
                <ScanLine className="w-4 h-4" /> Scanner / taper n° de lot
              </button>
              {showScan && (
                <div className="mt-2 flex gap-1">
                  <Input
                    ref={scanRef}
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleScan(); }}
                    placeholder="N° de lot…"
                    className="h-9 text-sm"
                    autoFocus
                    data-testid={`prep-scan-input-${line.distributor_id}-${line.slot_number}`}
                  />
                  <Button size="sm" onClick={handleScan} className="h-9 bg-[#1F4E3D]">OK</Button>
                </div>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {lotsLoading ? (
                <div className="p-3 text-center text-sm text-[#A3A6A3]">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Chargement…
                </div>
              ) : availableLots.length === 0 ? (
                <div className="p-3 text-center text-xs text-[#A3A6A3] italic">
                  Aucun lot dispo au Stock Conthey pour ce produit
                </div>
              ) : (
                availableLots.map((l) => {
                  const d = daysToDlc(l.dlc);
                  const crit = d !== null && d <= 3;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => selectLot(l)}
                      className={`w-full flex justify-between items-center px-3 py-2 text-sm hover:bg-[#F5F5F0] border-b border-[#F0F0EC] last:border-0 ${
                        lotId === l.id ? "bg-[#EAF4EE]" : ""
                      }`}
                      data-testid={`prep-lot-option-${l.id}`}
                    >
                      <div className="text-left">
                        <div className="font-mono font-medium">{l.lot_number}</div>
                        <div className="text-xs text-[#A3A6A3]">DLC {fmtDlc(l.dlc)} · qty {l.quantity}</div>
                      </div>
                      {crit && (
                        <Badge className="bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4] text-[10px]">
                          J{d >= 0 ? `+${d}` : d}
                        </Badge>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      </td>
      <td className={`px-2 py-3 text-center text-sm w-16 ${dlcCritical ? "text-[#C05C47] font-bold" : "text-[#575A57]"}`}>
        {fmtDlc(lotDlc)}
      </td>
      <td className="px-2 py-3 text-center w-12">
        {done ? (
          <CheckCircle2 className="w-6 h-6 text-[#1F4E3D] inline" />
        ) : (
          <div className="w-5 h-5 border-2 border-[#A3A6A3] rounded inline-block" />
        )}
      </td>
      <td className="px-1 py-3 text-center w-6 text-[10px]">
        {saveState === "saving" && <Loader2 className="w-3 h-3 animate-spin inline text-[#A3A6A3]" />}
        {saveState === "ok" && <span className="text-[#1F4E3D]">✓</span>}
        {saveState === "err" && <span className="text-[#C05C47]">!</span>}
      </td>
    </tr>
  );
}

export default function RefillTourPrepTablet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStates, setSaveStates] = useState({});
  // Phase 101gd — Reco picklist Nayax par distributeur.
  // BOUTON MANUEL : Alain préfère éviter les appels auto (le POST picklist crée
  // un brouillon côté Nayax à chaque appel, on ne veut pas polluer leur historique).
  // Structure : { [distributor_id]: { byProductId: { [product_id]: qty }, error?, loading? } }
  const [nayaxReco, setNayaxReco] = useState({});
  const [fetchingReco, setFetchingReco] = useState(false);

  const fetchAllRecos = async () => {
    if (!tour?.lines || !distributors.length) return;
    const distIds = [...new Set(tour.lines.map((l) => l.distributor_id))];
    const targets = distIds
      .map((did) => ({ did, dist: distributors.find((d) => d.id === did) }))
      .filter((x) => x.dist?.nayax_machine_id);
    if (targets.length === 0) {
      toast.info("Aucun distributeur du tour n'a de machine Nayax configurée");
      return;
    }
    setFetchingReco(true);
    // Marque tous comme loading
    setNayaxReco((prev) => {
      const next = { ...prev };
      for (const t of targets) next[t.did] = { loading: true };
      return next;
    });
    // Appels en parallèle (max 4 concurrent via chunks pour ne pas saturer Nayax)
    const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));
    let successCount = 0;
    let errorCount = 0;
    for (const batch of chunk(targets, 4)) {
      await Promise.all(batch.map(async ({ did }) => {
        try {
          const { data } = await api.post(`/nayax/picklist-recommendation/${did}`);
          if (!data.ok) {
            errorCount++;
            setNayaxReco((prev) => ({ ...prev, [did]: { error: data.error || "Erreur" } }));
            return;
          }
          const byProductId = {};
          for (const it of data.items || []) {
            if (it.product_id) byProductId[it.product_id] = it.recommended_pick_qty || 0;
          }
          successCount++;
          setNayaxReco((prev) => ({ ...prev, [did]: { byProductId, count: data.count } }));
        } catch (err) {
          errorCount++;
          setNayaxReco((prev) => ({ ...prev, [did]: { error: err.response?.data?.detail || err.message } }));
        }
      }));
    }
    setFetchingReco(false);
    if (successCount > 0) toast.success(`Reco Nayax récupérée pour ${successCount}/${targets.length} machine(s)`);
    if (errorCount > 0 && successCount === 0) toast.error(`Aucune reco récupérée (${errorCount} erreur${errorCount > 1 ? 's' : ''})`);
    else if (errorCount > 0) toast.warning(`${errorCount} machine(s) en erreur`);
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, d, p] = await Promise.all([
          api.get(`/refill-tours/${id}`),
          api.get("/distributors"),
          api.get("/products"),
        ]);
        setTour(t.data);
        setDistributors(d.data || []);
        setProducts(p.data || []);
      } catch (err) {
        toast.error(`Chargement impossible: ${err.response?.data?.detail || err.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Phase 101gd — Reco Nayax : plus d'auto-fetch, uniquement via le bouton
  // "Récupérer reco Nayax" (fetchAllRecos) pour éviter de créer des brouillons
  // parasites côté Nayax à chaque ouverture de la tablette.

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const distributorMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);

  // Group lines by distributor
  const grouped = useMemo(() => {
    if (!tour?.lines) return [];
    const byDist = {};
    for (const ln of tour.lines) {
      if ((ln.qty_planned ?? 0) <= 0) continue;
      if (!byDist[ln.distributor_id]) byDist[ln.distributor_id] = [];
      byDist[ln.distributor_id].push(ln);
    }
    return Object.entries(byDist).map(([did, lines]) => ({
      distributor: distributorMap[did] || { id: did, name: "?" },
      lines: lines.sort((a, b) => (a.slot_number || "").localeCompare(b.slot_number || "", "fr", { numeric: true })),
    }));
  }, [tour, distributorMap]);

  const lineKey = (ln) => `${ln.distributor_id}::${ln.slot_number}::${ln.product_id}`;

  const saveLine = useCallback(async (line, partial) => {
    const key = lineKey(line);
    setSaveStates((s) => ({ ...s, [key]: "saving" }));
    try {
      await api.put(`/refill-tours/${id}/line`, {
        distributor_id: line.distributor_id,
        product_id: line.product_id,
        slot_number: line.slot_number || null,
        ...partial,
      });
      setSaveStates((s) => ({ ...s, [key]: "ok" }));
      setTimeout(() => setSaveStates((s) => {
        const next = { ...s };
        if (next[key] === "ok") delete next[key];
        return next;
      }), 2000);
    } catch (err) {
      setSaveStates((s) => ({ ...s, [key]: "err" }));
      toast.error(`Erreur sauvegarde: ${err.response?.data?.detail || err.message}`);
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F4E3D]" />
      </div>
    );
  }
  if (!tour) {
    return <div className="p-8 text-center text-[#C05C47]">Tournée introuvable</div>;
  }

  const readOnly = tour.status === "CLOTUREE" || tour.status === "EN_LIVRAISON";

  const totalPlanned = grouped.reduce((sum, g) => sum + g.lines.reduce((s, l) => s + (l.qty_planned || 0), 0), 0);
  const totalLoaded = grouped.reduce((sum, g) => sum + g.lines.reduce((s, l) => s + (l.qty_loaded || 0), 0), 0);
  const totalReady = grouped.reduce((sum, g) => sum + g.lines.filter((l) => (l.qty_loaded || 0) > 0 && l.lot_allocations?.[0]?.lot_id).length, 0);
  const totalLines = grouped.reduce((sum, g) => sum + g.lines.length, 0);
  // Phase 101fj — Lignes chargées SANS lot → blocage validation (aucun FIFO auto)
  const missingLotLines = grouped.reduce(
    (sum, g) => sum + g.lines.filter((l) => (l.qty_loaded || 0) > 0 && !l.lot_allocations?.[0]?.lot_id).length,
    0,
  );

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header sticky */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[#1F4E3D] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/tournees-reappro/${id}`)}
            data-testid="prep-tablet-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div className="flex-1">
            <div className="font-heading text-lg font-semibold text-[#1F4E3D]">
              Préparation tournée · {tour.reference}
            </div>
            <div className="text-xs text-[#575A57]">
              {grouped.length} distributeur(s) · {totalLines} ligne(s) · {totalPlanned} barquette(s) à charger
            </div>
          </div>
          {/* Progress badge */}
          <div className="text-right">
            <div className="text-2xl font-bold text-[#1F4E3D]" data-testid="prep-progress-count">
              {totalReady} / {totalLines}
            </div>
            <div className="text-xs text-[#575A57]">lignes prêtes</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllRecos}
            disabled={fetchingReco || readOnly}
            data-testid="prep-tablet-fetch-nayax-reco-btn"
            className="border-[#1F4E3D]/40 text-[#1F4E3D]"
          >
            {fetchingReco ? "Nayax…" : "🎯 Reco Nayax"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`${api.defaults.baseURL}/refill-tours/${id}/prep-picklist.pdf`, "_blank")}
            data-testid="prep-tablet-pdf-btn"
            className="border-[#1F4E3D]/40 text-[#1F4E3D]"
          >
            <FileDown className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Sections par distributeur */}
      <div className="max-w-6xl mx-auto px-4 py-4 space-y-6">
        {readOnly && (
          <div className="bg-[#FBF4E9] border border-[#E8D9B8] text-[#8C6B2F] rounded-lg px-4 py-3 text-sm" data-testid="prep-tablet-readonly-banner">
            🔒 Tournée <b>{tour.status === "CLOTUREE" ? "clôturée" : "verrouillée en livraison"}</b> — la saisie est désactivée. Consultation uniquement.
          </div>
        )}
        {/* Phase 101fj — Bandeau anti-FIFO auto */}
        {!readOnly && missingLotLines > 0 && (
          <div
            className="bg-[#FBE9E4] border-2 border-[#C05C47] text-[#7A1F1F] rounded-lg px-4 py-3 text-sm flex items-start gap-3"
            data-testid="prep-tablet-missing-lots-banner"
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">
                {missingLotLines} ligne(s) chargée(s) sans lot sélectionné
              </div>
              <div className="text-xs mt-0.5">
                Aucun FIFO automatique — libre arbitre opérateur.
                Choisis le lot Conthey exact pour chaque ligne avant de verrouiller la tournée.
              </div>
            </div>
          </div>
        )}
        {grouped.length === 0 && (
          <div className="text-center py-12 text-[#A3A6A3]">
            <Package className="w-12 h-12 mx-auto mb-2 text-[#E5E5E0]" />
            Aucune ligne à préparer sur cette tournée.
          </div>
        )}
        {grouped.map((g) => {
          const planned = g.lines.reduce((s, l) => s + (l.qty_planned || 0), 0);
          const loaded = g.lines.reduce((s, l) => s + (l.qty_loaded || 0), 0);
          return (
            <div
              key={g.distributor.id}
              className="bg-white rounded-xl border border-[#E5E5E0] shadow-sm overflow-hidden"
              data-testid={`prep-section-${g.distributor.id}`}
            >
              <div className="px-4 py-3 bg-[#1F4E3D] text-white flex items-center justify-between">
                <div>
                  <div className="font-semibold text-base">{g.distributor.name}</div>
                  <div className="text-xs opacity-80">{g.distributor.address || g.distributor.city || ""}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold">{loaded} / {planned}</div>
                  <div className="text-[10px] opacity-80">barquettes</div>
                </div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="bg-[#F5F5F0] text-[10px] uppercase tracking-wider text-[#575A57]">
                  <tr>
                    <th className="px-2 py-2 text-center">Slot</th>
                    <th className="px-2 py-2 text-left">Produit</th>
                    <th className="px-2 py-2 text-center">Stock machine</th>
                    <th className="px-2 py-2 text-center">Capa</th>
                    <th className="px-2 py-2 text-center" title="Recommandation Nayax = ce que l'algo suggère de recharger">Reco Nayax</th>
                    <th className="px-2 py-2 text-center">Prévu</th>
                    <th className="px-2 py-2 text-center">Chargé</th>
                    <th className="px-2 py-2 text-left">N° Lot</th>
                    <th className="px-2 py-2 text-center">DLC</th>
                    <th className="px-2 py-2 text-center">OK</th>
                    <th className="px-1 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((ln) => {
                    const key = lineKey(ln);
                    const prod = productMap[ln.product_id] || {};
                    const recoData = nayaxReco[g.distributor.id] || {};
                    const nayaxRecoQty = recoData.byProductId?.[ln.product_id];
                    return (
                      <LineRow
                        key={key}
                        line={ln}
                        productName={prod.name || "?"}
                        productSku={prod.sku || ""}
                        onSave={saveLine}
                        saveState={saveStates[key]}
                        readOnly={readOnly}
                        nayaxRecoQty={nayaxRecoQty}
                        nayaxRecoLoading={recoData.loading}
                        nayaxRecoError={recoData.error}
                      />
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
